import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationAutomationGrossingShelfIntermediateShelfProcessModal
    from '@/modules/configurationModule/detail/automation/grossing-shelf/intermediate-shelf-process/components/modal/configuration-automation-grossing-shelf-intermediate-shelf-process-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import {
    ConfigurationAutomationGrossingShelfIntermediateShelfProcessGridState,
    initialConfigurationAutomationGrossingShelfIntermediateShelfProcessGridState
} from '@/modules/configurationModule/detail/automation/grossing-shelf/intermediate-shelf-process/components/grid/configuration-automation-grossing-shelf-intermediate-shelf-process-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import {
    ConfigurationIntermediateShelfSampleProcessAssignmentCreateRequest,
    ConfigurationIntermediateShelfSampleProcessAssignmentResponse
} from '@/modules/configurationModule/detail/automation/grossing-shelf/intermediate-shelf-process/services/configuration-intermediate-shelf-sample-process-assignment/configuration-intermediate-shelf-sample-process-assignment.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationAutomationGrossingShelfIntermediateShelfProcessModal
    }
})
export default class ConfigurationAutomationGrossingShelfIntermediateShelfProcessGrid extends BaseCrudServerSideComponent<ConfigurationIntermediateShelfSampleProcessAssignmentResponse> implements BaseCrud {
    @Ref() readonly intermediateShelfProcessModalWrapper!: typeof ModalWrapper

    state: ConfigurationAutomationGrossingShelfIntermediateShelfProcessGridState

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAutomationGrossingShelfIntermediateShelfProcessGridState()
    }

    /// SERVICES CALLS ///

    private async _createIntermediateShelfSampleProcessAssignment(createRequest: ConfigurationIntermediateShelfSampleProcessAssignmentCreateRequest): Promise<ConfigurationIntermediateShelfSampleProcessAssignmentResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _deleteIntermediateShelfSampleProcessAssignments(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: ConfigurationIntermediateShelfSampleProcessAssignmentCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createIntermediateShelfSampleProcessAssignment(createRequest)) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteIntermediateShelfSampleProcessAssignments(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(intermediateShelfSampleProcessAssignment => intermediateShelfSampleProcessAssignment.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.intermediateShelfProcessModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
